/**
 * The recurrence surface (P8-T2): the owner's schedules, the stop review, and a new schedule.
 *
 * It is its own component for the same reason `ReportsWorkspace` is: recurring entries are a
 * surface of their own, and keeping it out of `LedgerWorkspace` means the entry and category
 * surfaces (P7's half) can change without this one being rewritten.
 *
 * What it shows and where each fact comes from:
 *
 *   - **the schedules list** is `GET /api/v1/recurrences`, and a stopped one says so — the stop is
 *     terminal, and a list that showed a stopped schedule as an active one would be lying about the
 *     only irreversible thing on this screen;
 *   - **the stop review** is `GET /api/v1/recurrences/{id}`: every generated occurrence, its
 *     disposition, and what was edited about it. The set the default stop *will* cancel and the set
 *     a tick *can* add are two lists from the server (`cancellableEntryIds`,
 *     `editedExceptionEntryIds`), rendered apart, which is what "include edited exceptions only with
 *     explicit reviewed selection" looks like on screen;
 *   - **stop-only** is offered beside the default, and it names no entries: the same route with the
 *     other mode, and without a selection, because a selection alongside it is a contradiction the
 *     API refuses;
 *   - **a new schedule** goes through the same two-step review, and the retained-date overlap comes
 *     back from the API as a warning with a note. When it warns, the commit is gated on an
 *     acknowledgement — `IMPACT_CONFIRMATION_REQUIRED` is the protection, and this surface asks for
 *     the tick rather than hiding the refusal.
 *
 * Nothing here generates: the host's one-shot run is the only thing that ever extends a schedule, and
 * there is no route a page could call to make it run. A creation does fill the window its own review
 * showed — the commit writes exactly those occurrences and the preview is where they were read — and
 * the surface says so rather than leaving a reader to guess which half wrote what.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  RecurrencePreviewBody,
  RecurrenceStopMode,
  RecurrenceTemplateBody,
} from '@saldo/contracts';

import {
  commitRecurrence,
  createRecurrencePreview,
  describeLedgerFailure,
  listAccounts,
  listRecurrences,
  readRecurrence,
  type LedgerFailureKind,
} from './client';
import {
  createPreviewView,
  isCreatePreview,
  isStopPreview,
  recurrenceRowView,
  stopPreviewView,
  stopReview,
  toRecurrenceCreate,
  toRecurrenceStop,
  type RecurrenceForm,
} from './recurrences';
import { useAuthenticatedSession } from '../session/SessionBoundary';

/** The failure kind of anything a request throws; anything else is the server being away. */
function failureKind(error: unknown): LedgerFailureKind {
  const kind = (error as { kind?: LedgerFailureKind }).kind;
  return kind ?? 'unavailable';
}

/** A fresh idempotency key for one write. Nothing here reuses a key across two intents. */
function newKey(): string {
  return crypto.randomUUID();
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export function RecurrenceWorkspace() {
  const session = useAuthenticatedSession();
  const queryClient = useQueryClient();

  const [openId, setOpenId] = useState<string | null>(null);
  const [mode, setMode] = useState<RecurrenceStopMode>('stop-and-cancel');
  const [selected, setSelected] = useState<readonly string[]>([]);
  const [preview, setPreview] = useState<RecurrencePreviewBody | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<RecurrenceForm>({
    accountId: '',
    categoryId: null,
    description: '',
    amount: '',
    kind: 'expense',
    frequency: 'monthly',
    anchorDate: today(),
    endDate: '',
  });

  const schedules = useQuery({
    queryKey: ['ledger', 'recurrences'],
    queryFn: ({ signal }) => listRecurrences(session.csrfToken, signal),
  });
  const accounts = useQuery({
    queryKey: ['ledger', 'accounts'],
    queryFn: ({ signal }) => listAccounts(session.csrfToken, signal),
  });

  const detail = useQuery({
    queryKey: ['ledger', 'recurrences', openId],
    queryFn: ({ signal }) => readRecurrence(session.csrfToken, openId as string, signal),
    enabled: openId !== null,
  });

  const review = useMemo(
    () => (detail.data === undefined ? null : stopReview(detail.data, mode, selected)),
    [detail.data, mode, selected],
  );

  const open = (template: RecurrenceTemplateBody) => {
    setOpenId(template.id);
    setMode('stop-and-cancel');
    setSelected([]);
    setPreview(null);
    setAcknowledged(false);
    setNotice(null);
    setError(null);
  };

  // The two reviews are separate mutations rather than one discriminated one: they are different
  // requests, and a single mutation would let a stop review be committed by a creation's callback.
  const stopReviewMutation = useMutation({
    mutationFn: async (input: { templateId: string; mode: RecurrenceStopMode; selection: readonly string[] }) => {
      const mapped = toRecurrenceStop(input.templateId, input.mode, input.selection);
      if (!mapped.ok) throw Object.assign(new Error(mapped.error), { local: mapped.error });
      return createRecurrencePreview(session.csrfToken, mapped.body, {
        idempotencyKey: newKey(),
        ifMatch: undefined,
      });
    },
    onSuccess: (stored) => {
      setPreview(stored);
      setError(null);
      setNotice(null);
    },
    onError: (thrown) => {
      const local = (thrown as { local?: string }).local;
      setError(local ?? describeLedgerFailure(failureKind(thrown)));
      setPreview(null);
    },
  });

  const createReviewMutation = useMutation({
    mutationFn: async (draft: RecurrenceForm) => {
      const mapped = toRecurrenceCreate(draft);
      if (!mapped.ok) throw Object.assign(new Error(mapped.error), { local: mapped.error });
      return createRecurrencePreview(session.csrfToken, mapped.body, {
        idempotencyKey: newKey(),
        ifMatch: undefined,
      });
    },
    onSuccess: (stored) => {
      setPreview(stored);
      setError(null);
      setNotice(null);
    },
    onError: (thrown) => {
      const local = (thrown as { local?: string }).local;
      setError(local ?? describeLedgerFailure(failureKind(thrown)));
      setPreview(null);
    },
  });

  const commitMutation = useMutation({
    mutationFn: async (stored: RecurrencePreviewBody) => {
      if (isStopPreview(stored)) {
        return commitRecurrence(
          session.csrfToken,
          stored.id,
          false,
          { idempotencyKey: newKey(), ifMatch: stored.revision },
        );
      }
      return commitRecurrence(session.csrfToken, stored.id, acknowledged, {
        idempotencyKey: newKey(),
      });
    },
    onSuccess: async (template) => {
      setPreview(null);
      setAcknowledged(false);
      setSelected([]);
      setNotice(
        template.status === 'stopped'
          ? `${template.description} is stopped and will never generate again.`
          : `${template.description} is scheduled. The occurrences its review showed are written; later ones come from the generation run.`,
      );
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['ledger', 'recurrences'] });
      if (openId !== null) await detail.refetch();
    },
    onError: (thrown) => setError(describeLedgerFailure(failureKind(thrown))),
  });

  const rows = schedules.data?.recurrences ?? [];
  const stopView = preview !== null && isStopPreview(preview) ? stopPreviewView(preview) : null;
  const createView = preview !== null && isCreatePreview(preview) ? createPreviewView(preview) : null;

  return (
    <section aria-labelledby="recurrence-heading" data-testid="recurrence-workspace">
      <h2 id="recurrence-heading">Recurring entries</h2>
      <p className="hint">
        A schedule writes its occurrences when the generation run happens; this page reads and stops
        them, it never generates one.
      </p>

      {schedules.isPending ? <p>Reading your schedules…</p> : null}
      {schedules.isError ? (
        <p className="notice bad" data-testid="recurrence-list-error">
          {describeLedgerFailure(failureKind(schedules.error))}
        </p>
      ) : null}
      {schedules.data !== undefined && rows.length === 0 ? (
        <p data-testid="recurrence-empty">No schedule yet.</p>
      ) : null}

      <ul className="directory-list" data-testid="recurrence-list">
        {rows.map((template) => {
          const view = recurrenceRowView(template);
          return (
            <li key={view.id}>
              <button
                type="button"
                data-testid="recurrence-item"
                data-status={view.status}
                aria-pressed={openId === view.id}
                onClick={() => open(template)}
              >
                {view.description}
                <span className="muted">
                  {' '}
                  · {view.direction} · {view.cadence} ·{' '}
                  {view.active ? 'active' : 'stopped'} · {view.coverage}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      {detail.isError ? (
        <p className="notice bad" data-testid="recurrence-detail-error">
          {describeLedgerFailure(failureKind(detail.error))}
        </p>
      ) : null}

      {review !== null && detail.data !== undefined ? (
        <div data-testid="recurrence-detail">
          <h3>{detail.data.template.description}</h3>
          <p className="hint" data-testid="recurrence-status">
            {detail.data.template.status === 'active'
              ? 'Active: it still generates.'
              : 'Stopped: it never generates again.'}
          </p>

          <table data-testid="occurrence-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Date</th>
                <th>Amount</th>
                <th>This stop</th>
                <th>Cancel it too</th>
              </tr>
            </thead>
            <tbody>
              {review.rows.map((row) => (
                <tr key={row.ordinal} data-testid="occurrence-row" data-disposition={row.disposition}>
                  <td>{row.ordinal}</td>
                  <td data-testid="occurrence-date">
                    {row.occurrenceDate}
                    {row.dueDateNote !== null ? (
                      <span data-testid="occurrence-due"> — {row.dueDateNote}</span>
                    ) : null}
                  </td>
                  <td className="money" data-testid="occurrence-amount">
                    {row.amount ?? '—'}
                  </td>
                  <td data-testid="occurrence-state">
                    {row.state}
                    {row.editedSummary !== null ? ` ${row.editedSummary}` : ''}
                  </td>
                  <td>
                    {row.selectable && mode === 'stop-and-cancel' ? (
                      <input
                        type="checkbox"
                        data-testid="occurrence-select"
                        checked={selected.includes(row.entryId as string)}
                        onChange={(event) => {
                          const entryId = row.entryId as string;
                          setSelected((current) =>
                            event.target.checked
                              ? [...current, entryId]
                              : current.filter((id) => id !== entryId),
                          );
                        }}
                      />
                    ) : (
                      <span aria-hidden="true">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <dl className="status-grid">
            <dt>The default stop cancels</dt>
            <dd data-testid="stop-default-count">{review.defaultCount}</dd>
            <dt>You added by ticking</dt>
            <dd data-testid="stop-selected-count">{review.selectedCount}</dd>
            <dt>Left alone</dt>
            <dd data-testid="stop-protected-count">{review.protectedCount}</dd>
          </dl>

          <fieldset className="field">
            <legend>What should happen to the generated entries?</legend>
            <label className="field">
              <input
                type="radio"
                name="stop-mode"
                data-testid="stop-mode-default"
                checked={mode === 'stop-and-cancel'}
                onChange={() => {
                  setMode('stop-and-cancel');
                  setPreview(null);
                }}
              />
              Stop and cancel the {review.defaultCount + review.selectedCount} occurrence
              {review.defaultCount + review.selectedCount === 1 ? '' : 's'} shown above
            </label>
            <label className="field">
              <input
                type="radio"
                name="stop-mode"
                data-testid="stop-mode-only"
                checked={mode === 'stop-only'}
                onChange={() => {
                  setMode('stop-only');
                  setSelected([]);
                  setPreview(null);
                }}
              />
              Stop only — keep every generated entry, cancel nothing
            </label>
          </fieldset>

          <div className="actions">
            <button
              type="button"
              className="primary"
              data-testid="stop-review"
              disabled={stopReviewMutation.isPending || detail.data.template.status !== 'active'}
              onClick={() =>
                stopReviewMutation.mutate({
                  templateId: detail.data.template.id,
                  mode,
                  selection: selected,
                })
              }
            >
              {stopReviewMutation.isPending ? 'Reviewing…' : 'Review the stop'}
            </button>
          </div>
        </div>
      ) : null}

      {stopView !== null ? (
        <div data-testid="stop-preview">
          <p data-testid="stop-preview-summary">{stopView.summary}</p>
          <dl className="status-grid">
            <dt>Entries cancelled</dt>
            <dd data-testid="stop-preview-count">{stopView.willCancelCount}</dd>
            <dt>Because you ticked them</dt>
            <dd data-testid="stop-preview-selected">{stopView.selected.length}</dd>
          </dl>
          <ul data-testid="stop-preview-entries">
            {stopView.willCancel.map((entryId) => (
              <li key={entryId} data-testid="stop-preview-entry">
                {entryId}
              </li>
            ))}
          </ul>
          <div className="actions">
            <button
              type="button"
              className="primary"
              data-testid="stop-confirm"
              disabled={commitMutation.isPending}
              onClick={() => commitMutation.mutate(preview as RecurrencePreviewBody)}
            >
              {commitMutation.isPending ? 'Stopping…' : 'Stop this schedule'}
            </button>
          </div>
        </div>
      ) : null}

      {createView !== null ? (
        <div data-testid="create-preview">
          <p data-testid="create-preview-window">
            {createView.occurrences.length} occurrence
            {createView.occurrences.length === 1 ? '' : 's'} from {createView.firstDate} to{' '}
            {createView.lastDate}
          </p>
          <p data-testid="create-preview-note">{createView.note}</p>
          {createView.hasOverlap ? (
            <ul data-testid="create-overlap">
              {createView.overlaps.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          ) : null}
          {createView.requiresConfirmation ? (
            <label className="field">
              <input
                type="checkbox"
                data-testid="create-acknowledge"
                checked={acknowledged}
                onChange={(event) => setAcknowledged(event.target.checked)}
              />
              I have read the overlap: these days already carry entries.
            </label>
          ) : null}
          <div className="actions">
            <button
              type="button"
              className="primary"
              data-testid="create-confirm"
              disabled={commitMutation.isPending || (createView.requiresConfirmation && !acknowledged)}
              onClick={() => commitMutation.mutate(preview as RecurrencePreviewBody)}
            >
              {commitMutation.isPending ? 'Saving…' : 'Create this schedule'}
            </button>
          </div>
        </div>
      ) : null}

      {notice !== null ? (
        <p className="notice" data-testid="recurrence-notice">
          {notice}
        </p>
      ) : null}
      {error !== null ? (
        <p className="notice bad" data-testid="recurrence-error">
          {error}
        </p>
      ) : null}

      <h3>New schedule</h3>
      <div className="field" data-testid="recurrence-create">
        <label className="field">
          Account
          <select
            data-testid="create-account"
            value={form.accountId}
            onChange={(event) => setForm({ ...form, accountId: event.target.value })}
          >
            <option value="">Choose an account</option>
            {(accounts.data?.accounts ?? []).map((account) => (
              <option key={account.id} value={account.id}>
                {account.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          Description
          <input
            data-testid="create-description"
            value={form.description}
            onChange={(event) => setForm({ ...form, description: event.target.value })}
          />
        </label>
        <label className="field">
          Amount
          <input
            data-testid="create-amount"
            inputMode="decimal"
            value={form.amount}
            onChange={(event) => setForm({ ...form, amount: event.target.value })}
          />
        </label>
        <label className="field">
          Direction
          <select
            data-testid="create-kind"
            value={form.kind}
            onChange={(event) =>
              setForm({ ...form, kind: event.target.value === 'income' ? 'income' : 'expense' })
            }
          >
            <option value="expense">Money out</option>
            <option value="income">Money in</option>
          </select>
        </label>
        <label className="field">
          Repeats
          <select
            data-testid="create-frequency"
            value={form.frequency}
            onChange={(event) =>
              setForm({
                ...form,
                frequency: event.target.value as RecurrenceForm['frequency'],
              })
            }
          >
            <option value="monthly">Every month</option>
            <option value="weekly">Every week</option>
            <option value="biweekly">Every two weeks</option>
          </select>
        </label>
        <label className="field">
          First occurrence
          <input
            data-testid="create-anchor"
            type="date"
            value={form.anchorDate}
            onChange={(event) => setForm({ ...form, anchorDate: event.target.value })}
          />
        </label>
        <label className="field">
          Last occurrence (optional)
          <input
            data-testid="create-end"
            type="date"
            value={form.endDate}
            onChange={(event) => setForm({ ...form, endDate: event.target.value })}
          />
        </label>
        <div className="actions">
          <button
            type="button"
            data-testid="create-review"
            disabled={createReviewMutation.isPending}
            onClick={() => createReviewMutation.mutate(form)}
          >
            {createReviewMutation.isPending ? 'Reviewing…' : 'Review this schedule'}
          </button>
        </div>
      </div>
    </section>
  );
}
